import React, { useContext, useState, useEffect } from 'react';
import useSWR from 'swr';
import { Layout, Badge, Row, Divider } from 'antd';
import _ from 'lodash';
import gradient from 'random-gradient'
import { Textfit } from '@kyo_ago/react-textfit';
import Ratio from 'react-ratio';

import Icon from './icon/Icon';
import CropperModal from './cropper/Modal';
import StoryCard from './story/StoryCard';
import ProfileAvatar from './common/ProfileAvatar';
import DynamicGrid from './common/DynamicGrid';
import TextInput from './forms/TextInput';
import {callApi} from '../helpers/Api';
import FollowButton from './reader/FollowButton';
import useWindowDimensions from '../helpers/useWindowDimensions';
import {mapGridColBySize} from '../helpers/NumberFormat';
import NoStretchDiv from '../helpers/NoStretchDiv';
import {updateCloudinaryUrl} from '../helpers/Cloudinary';
import UserContext from '../context/UserContext';

const { Content } = Layout;

const Profile = ({viewingPublic, userData, storyData}) => {
    const {loggedInUser, setLoggedInUser} = useContext(UserContext);
    const [user, setUser] = useState(null);
    const [myStories, setMyStories] = useState([]);
    const [backgroundUrl, setBackgroundUrl] = useState(null);
    const [avatarUrl, setAvatarUrl] = useState(null);
    // determines your own editable profile vs public
    const [editingDesc, setEditingDesc] = useState(false);
    const {data: followData} = useSWR(userData ? '/api/user_account_count/follow/' + userData.username : null);
    const followCount = followData && followData.length ? followData[0].follow_count : 0;
    const { width } = useWindowDimensions();

    useEffect(() => {
        setMyStories(storyData)
        setUser(userData)
        setBackgroundUrl(userData?.background_picture || null)
        setAvatarUrl(userData?.profile_picture || null)
    }, [userData, storyData])

    const myStoryCards = myStories.map((story, i) => {
        return (
            <StoryCard story={story} own={!viewingPublic ? true : false} />
        );
    })

    return (
        <Layout>
            {user ? (
                <Ratio 
                    ratio={ 16 / 3 } 
                    style={backgroundUrl ? {
                        background: `url(${updateCloudinaryUrl(backgroundUrl)})`,
                        backgroundSize: 'cover',
                    } : {
                        background: gradient(user.username + user.id)
                    }}
                >
                    {(viewingPublic) ? null :(
                    <CropperModal 
                        title='Upload Profile Dashboard'
                        url='/api/upload/profileHeader'
                        opener={
                            <div className="clickable">
                                <Icon size={2} name="pencil-alt" color="white" style={{
                                    cursor:'pointer', 
                                    padding: '5px', 
                                    background: 'black', 
                                    borderRadius: '20px',
                                    margin: '5px',
                                    zIndex: 1,
                                }} />
                            </div>
                        }
                        updatePhoto={(image) => {
                            setBackgroundUrl(image)
                            if(viewingPublic) {
                                setUser(Object.assign(user, {background_picture: image}))
                            } else {
                                setLoggedInUser(Object.assign(loggedInUser, {background_picture: image}))
                            }
                        }}
                        aspect={16/3}
                    />
                    )}
                    {backgroundUrl ? null : (
                        <Textfit style={{
                            width: '100%',
                            height: '100%',
                            paddingBottom: '42px',
                            color: '#fff', 
                            textAlign: 'center', 
                            margin: 'auto',
                        }}>
                            {user.username}
                        </Textfit>
                    )}
                </Ratio>
            ) : null}
            <Layout>
                <Content>
                    <Row type='flex' justify='center' style={{marginTop: '-42px'}}>
                        <div className="heavy-darkable" style={{padding: '10px', borderRadius: '4px', zIndex: '1'}}>
                            {(viewingPublic) ? (
                            <ProfileAvatar avatarUrl={updateCloudinaryUrl(avatarUrl)} />
                            ) :(
                            <Badge count={
                                <CropperModal 
                                    title='Upload Profile Photo'
                                    url='/api/upload/profilePhoto'
                                    opener={
                                        <div className="clickable">
                                            <Icon name="pencil-alt" style={{cursor:'pointer'}} />
                                        </div>
                                    }
                                    updatePhoto={(image) => {
                                        setAvatarUrl(image)
                                        if(viewingPublic) {
                                            setUser(Object.assign(user, {profile_picture: image}))
                                        } else {
                                            setLoggedInUser(Object.assign(loggedInUser, {profile_picture: image}))
                                        }
                                    }}
                                    aspect={1}
                                    outerClass='ant-scroll-number-custom-component'
                                />
                            }>
                                <ProfileAvatar avatarUrl={updateCloudinaryUrl(avatarUrl)} />
                            </Badge>
                            )}
                        </div>
                    </Row>
                    <Row type='flex' justify='center'>
                        <h1 style={{fontSize: '25px', marginBottom: '0', textAlign: 'center'}}>
                            {user ? user.username : null}
                        </h1>
                    </Row>
                    <Row type='flex' justify='center'>
                        <p style={{marginTop: '0'}}>{followCount + " followers"}</p>
                    </Row>
                    <Row type='flex' justify='center'>
                        {(viewingPublic) ? (
                            <p className='line-break secondary'>
                                {user ? user.description : null}
                            </p>
                        ) : (
                            !editingDesc ? (
                                <Badge count={
                                    <div className='ant-scroll-number-custom-component clickable'>
                                        <Icon 
                                            name="pencil-alt" 
                                            style={{cursor:'pointer', marginLeft:'10px'}} 
                                            onClick={() => {setEditingDesc(true)}}
                                        />
                                    </div>
                                }>
                                    <p className='line-break secondary'>{user ? user.description ? user.description : 'no description given' : null}</p>
                                </Badge>
                            ) : (
                                <TextInput 
                                    label="Description"
                                    className="light-darkable"
                                    placeholder="Enter description here..."
                                    buttonText="Save"
                                    initialValue={user.description}
                                    handleSubmit={(description) => {
                                        callApi('/api/own/user_account',{
                                            method: 'PUT',
                                            body: JSON.stringify({description: description})
                                        })
                                        .then((res) => {
                                            if(res === 1){
                                                var temp = _.clone(user);
                                                temp.description = description;
                                                setUser(temp);
                                            }
                                        });
                                        setEditingDesc(false)
                                    }}
                                />
                            )                            
                        )}
                    </Row>
                    <NoStretchDiv>
                        <Divider orientation="left">
                            <p className="text-darkable">Stories {user && (user.id !== loggedInUser?.id) ? (
                                    <FollowButton 
                                        user_account_id={user.id}
                                        onFollow={() => {
                                            setFollowCount(followCount + 1)
                                        }}
                                        onUnfollow={() => {
                                            setFollowCount(followCount - 1)
                                        }}
                                    />
                                ) : null}
                            </p>
                        </Divider>
                        <DynamicGrid 
                            gutter={16}
                            colCount={mapGridColBySize(width)}
                            colClassName="mobile-stretch"
                            cells={myStoryCards}
                        />
                    </NoStretchDiv>
                </Content>
            </Layout>
        </Layout>
    );
}

export default Profile;